import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sunstone/ui';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

interface Item {
  id: string;
  displayName: string;
}

interface SearchableComboboxProps<T extends Item = Item> {
  value: string;
  onChange: (value: string) => void;
  items: T[];
  search: string;
  onSearchChange: (search: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  autoOpen?: boolean;
  autoOpenDelay?: number;
  /** Overrides an item's rendered row content; falls back to plain displayName. */
  renderItem?: (item: T) => ReactNode;
  /** Shows a loading row in place of the item list/empty message. */
  isLoading?: boolean;
  /** Rendered in a footer strip below the list, e.g. a "Showing N of M" pagination hint. */
  footer?: ReactNode;
}

export function SearchableCombobox<T extends Item = Item>({
  value,
  onChange,
  items,
  search,
  onSearchChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results found.',
  autoOpen = false,
  autoOpenDelay = 0,
  renderItem,
  isLoading = false,
  footer,
}: SearchableComboboxProps<T>) {
  const [open, setOpen] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally fires once on mount
  useEffect(() => {
    if (!autoOpen || value) return;
    const timer = setTimeout(() => setOpen(true), autoOpenDelay);
    return () => clearTimeout(timer);
  }, []);

  const selectedLabel = items.find((item) => item.id === value)?.displayName;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          {selectedLabel || <span className="text-muted-foreground">{placeholder}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={onSearchChange}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {items.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={(val) => {
                        onChange(val);
                        setOpen(false);
                      }}
                    >
                      <Check className={value === item.id ? 'opacity-100' : 'opacity-0'} />
                      {renderItem ? renderItem(item) : item.displayName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
          {footer && (
            <div className="border-t px-3 py-2 text-xs text-muted-foreground">{footer}</div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
